import React, { FC, memo, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { HttpResponseStatusCodes } from '@hobt/constants';
import {
  Accordion,
  Button,
  ButtonSize,
  ButtonVariant,
  CmhcIcon,
  CmhcIconSize,
  TabFields,
  TabsType,
  Tab,
  NotificationBanner,
  ToastNotification,
  ToastNotificationVariant,
  ActionDropdown,
  Modal,
  ComboBox,
  ComboboxOption,
  LargeFormField
} from '@cmhc-schl/hearth-ui';
import { Box, ClickAwayListener, Stack, Typography } from '@mui/material';
import HbtBreadcrumbComponent from 'Feature/V2/HbtBreadcrumb/components/HbtBreadcrumbComponent';
import { ApplicationLoanCompositionDetails } from 'Feature/V2/HbtPortfolioManagement/components/HbtPortfolioManagementRequestOverview/components/ApplicationLoanCompositionDetails/ApplicationLoanCompositionDetails';
import { ApplicationOverviewDetails } from 'Feature/V2/HbtPortfolioManagement/components/HbtPortfolioManagementRequestOverview/components/ApplicationOverviewDetails/ApplicationOverviewDetails';
import { Helmet } from 'react-helmet';
import { useTranslation } from 'react-i18next';
import HbtMasterPoolPremiumsAndTaxes from '../HbtMasterPoolPremiumsAndTaxes';
import LoanStatusPill from '../../shared/LoanStatusPill';
import { loanStatusMapping } from '../HbtMasterPoolTable/utils/LoanStatusMapping';
import DocumentAndResourcesFlyout from './components/Flyout/DocumentsAndResourcesFlyout';
import { useLoansCompositions, useMasterPoolTableDetails } from './hooks';
import styles from './styles.module.scss';
import {
  checkForApprovalDateRules,
  checkForWithdrawRules,
  checkForReverseFinalizationRules,
  checkForReverseWithdrawRules,
  convertMasterPoolDetails,
  getLoanStatusLabel,
  getMasterPoolAccountStatusCode,
  getMasterPoolCmhcAccountNumber,
  getMasterPoolCmhcLenderCode,
  getUrlParam,
  checkForApprovalRules,
  checkForFinalizeRules
} from './utils/masterPoolDetailsUtils';
import { useLoanComposition } from './hooks/useLoanCompositions';
import HbtMasterPoolLoansInventoryTable from '../HbtMasterPoolLoansInventoryTable';
import { NotificationBannerType } from 'Feature/V2/HbtPIRequest/components/HbtPIRequestPage/types';

import { useRightClickContextMenu } from './hooks/useRightClickContextMenu';
import { OfferDate } from './components/OfferDate';
import {
  usePatchMasterPoolOverrideApprovalDate,
  usePatchMasterPoolOverrideApprove,
  usePatchMasterPoolOverrideFinalize,
  usePatchMasterPoolOverrideOfferDate,
  usePatchMasterPoolOverrideCitadalDate,
  usePatchMasterPoolReverseApproval,
  usePatchMasterPoolReverseFinalization,
  usePatchMasterPoolWithDraw,
  usePatchReverseWithdrawalMasterPool,
  usePatchReRunPricingScenario,
  usePatchMasterPoolReleaseWorkflowState
} from './hooks/usePatchMasterPoolOverrideOfferDate';

import { getSearchParams } from 'Feature/V2/Shared_Portfolio/components/SearchFlyout/utils/getSearchParams';
import { setSearchParams } from 'Feature/V2/Shared_Portfolio/components/SearchFlyout/utils/setSearchParams';
import { useLocation, useHistory } from 'react-router-dom';
import { PortfolioLoanStatusCode } from 'Feature/V2/HbtPortfolioManagement/components/HbtPortfolioManagementRequestOverview/api/types';
import { OfferDateConfig } from './components/OfferDate/types';
import { Remark, ToastNotificationConfig, WithdrawalOption } from './types';
import {
  WithdrawalMaxValue,
  WithdrawalRemarks,
  withdrawalValues
} from './utils/masterPoolConstants';
import { validateWorkFlowOptions, createOption } from './utils/workFlowOptions';
import { MasterPoolDataRows } from './components/MasterPoolDataRows';
import HbtMasterPoolNumberAddRemarksFlyout from '../HbtMasterPool/component/HbtMasterPoolNumberAddRemarks';
import { LatestRemark } from '../HbtMasterPool/types';
import ViewHistoricalRemarksFlyout from '../HbtMasterPoolLoansInventoryTable/components/ViewHistoricalRemarksFlyout/ViewHistoricalRemarksFlyout';
import { MasterPoolStatusEnum } from '../HbtMasterPoolTable/types';
import { useMasterPoolPremiumsBreakdown } from '../HbtMasterPoolPremiumsAndTaxes/hooks';
import { navigateToScenarioPricing } from '../HbtMasterPoolPremiumsAndTaxes/components/HbtMasterPoolPremiumsAndTaxesSection/Utils/NavigateToScenarioPricing';
import i18n from 'i18next';
import { useQuery } from '@tanstack/react-query';
import { RemarkItem } from '../HbtMasterPoolLoansInventoryTable/types';
import { getMasterPoolHistoricalRemarks } from '../HbtMasterPoolLoansInventoryTable/components/ViewHistoricalRemarksFlyout/api/MasterPoolNumberViewHistoricalRemarksService';
import { useAuthenticationContext } from 'Foundation/Authentication';
import RichTextBody from 'Feature/V2/Shared_Portfolio/components/RichTextBody/RichTextBody';
import ResetWorkflowStateIcon from 'Feature/V2/Shared_Portfolio/components/ResetIcon';
import { HTTP_STATUS_CONFLICT } from '../HbtMasterPool/ValidationInventoryService';

type WorkflowResponseEnvelope = {
  statusCode?: number;
  status?: number;
  data?: unknown;
  error?: unknown;
};

const getWorkflowStatusCode = (error: any): number | undefined => {
  return error?.status;
};

const isWorkflowConflictError = (error: unknown): boolean => {
  const statusCode = getWorkflowStatusCode(error);
  return statusCode === HTTP_STATUS_CONFLICT;
};

const HbtMasterPoolDetails: FC = () => {
  // Translations
  const { t } = useTranslation('HbtMasterPoolDetails');
  const { t: ro } = useTranslation('HbtPortfolioManagementRequestOverview');
  const location = useLocation();
  const history = useHistory();

  // Constants & Helpers
  const masterPoolCmhcAccountNumber = getMasterPoolCmhcAccountNumber();
  const masterPoolLenderCode = getMasterPoolCmhcLenderCode();
  const requestType = getUrlParam('requestType');
  const { dataType, searchQuery } = getSearchParams(location.search);

  const authenticationContext = useAuthenticationContext();

  const {
    data: remarksResponse,
    isLoading: isRemarksLoading,
    isError: isRemarksError
  } = useQuery<RemarkItem[]>({
    queryKey: ['masterpool-number-historical-remarks', +masterPoolCmhcAccountNumber],
    queryFn: () =>
      getMasterPoolHistoricalRemarks({
        authenticationContext,
        masterPoolNumber: +masterPoolCmhcAccountNumber
      })
  });

  const [openHistoricalRemarksFlyout, setOpenHistoricalRemarksFlyout] = useState<boolean>(false);

  const toggleHistoricalRemarksDrawer = (newOpen: boolean) => {
    setOpenHistoricalRemarksFlyout(newOpen);
  };
  const [openMasterPoolNumberRemarksFlyout, setOpenMasterPoolNumberRemarksFlyout] =
    useState<boolean>(false);

  const [openReRunPricingScenarioModal, setOpenReRunPricingScenarioModal] =
    useState<boolean>(false);

  const params = Object.fromEntries(
    Object.entries(searchQuery).filter(([key]) => {
      if (key === 'masterPoolCmhcAccountNumber' && dataType !== 'Master Pool Number') {
        return false;
      }
      return true;
    })
  );

  const searchParams =
    dataType && searchQuery ? setSearchParams({ dataType, query: { ...params } }) : '';

  const shouldSkipSearchPageFromBreadcrumb =
    dataType === 'Master Pool Number' || dataType === 'Sub Pool Number' || !dataType;
  const skippedHrefs = shouldSkipSearchPageFromBreadcrumb
    ? [`/pages/master-pool/search?${searchParams}`]
    : [];

  // Queries
  const { data, isLoading, isError, refetch } = useMasterPoolTableDetails(
    masterPoolCmhcAccountNumber
  );
  const masterPoolAccountStatusCode = useMemo(() => data?.[0]?.accountStatusCode ?? '', [data]);

  const {
    data: loanCompositions,
    isLoading: isLoanLoading,
    isError: isLoanError
  } = useLoansCompositions(masterPoolCmhcAccountNumber);

  // Memoized / Derived Values
  const convertMasterPool =
    !isLoading && !isError && Array.isArray(data) && data.length > 0
      ? convertMasterPoolDetails(data)
      : undefined;

  const {
    data: premiumAndTaxesData,
    isLoading: premiumAndTaxesDataLoading,
    isError: premiumAndTaxesError
  } = useMasterPoolPremiumsBreakdown(masterPoolCmhcAccountNumber);

  const shouldShowPremiumsAndTaxes = useMemo(() => {
    return (
      (!!premiumAndTaxesData?.[0].premiumAmountReceived &&
        premiumAndTaxesData?.[0].premiumAmountReceived > 0) ||
      (premiumAndTaxesData?.[0].requestType === '0' &&
        [
          MasterPoolStatusEnum.Approved,
          MasterPoolStatusEnum.Finalized,
          MasterPoolStatusEnum.Lock,
          MasterPoolStatusEnum.Expired
        ].includes(premiumAndTaxesData?.[0].accountStatusCode as MasterPoolStatusEnum))
    );
  }, [premiumAndTaxesData]);

  const shouldHidePremiumsAndTaxes = useMemo(
    () =>
      !!premiumAndTaxesData?.[0].premiumAmountReceived &&
      !!(premiumAndTaxesData?.[0].premiumAmountReceived <= 0) &&
      premiumAndTaxesData?.[0].requestType === '0' &&
      premiumAndTaxesData?.[0].accountStatusCode === MasterPoolStatusEnum.Withdrawn,
    [premiumAndTaxesData]
  );

  const noDataAvailable = useMemo(
    () =>
      !!premiumAndTaxesData?.[0].premiumAmountReceived &&
      !!(premiumAndTaxesData?.[0].premiumAmountReceived <= 0) &&
      premiumAndTaxesData?.[0].requestType === '0' &&
      premiumAndTaxesData?.[0].accountStatusCode === MasterPoolStatusEnum.Risked,
    [premiumAndTaxesData]
  );

  const { loanSummaryError, showLoanCompositionError, convertedLoansDetails } = useLoanComposition(
    loanCompositions,
    t
  );

  //  Mutations
  const { mutateAsync: approvalDate } = usePatchMasterPoolOverrideApprovalDate();
  const { mutateAsync: offerDate } = usePatchMasterPoolOverrideOfferDate();
  const { mutateAsync: approve } = usePatchMasterPoolOverrideApprove();
  const { mutateAsync: finalize } = usePatchMasterPoolOverrideFinalize();
  const { mutateAsync: citadalDate } = usePatchMasterPoolOverrideCitadalDate();
  const { mutateAsync: reverse } = usePatchMasterPoolReverseApproval();
  const { mutateAsync: withdrawMasterPool } = usePatchMasterPoolWithDraw();
  const { mutateAsync: reverseFinalization } = usePatchMasterPoolReverseFinalization();
  const { mutateAsync: reverseWithdraw } = usePatchReverseWithdrawalMasterPool();
  const { mutateAsync: reRunPricingScenario } = usePatchReRunPricingScenario();
  const { mutateAsync: resetWorkflowState } = usePatchMasterPoolReleaseWorkflowState();

  // State
  const [activeTab, setActiveTab] = useState(0);
  const [pmRequestOverviewTabList, setPmRequestOverviewTabList] = useState<TabFields[]>([]);
  const [contextAnchorEl, setContextAnchorEl] = useState<HTMLElement | null>(null);
  const [contextAnchorWorkFlowActions, setContextAnchorWorkFlowActions] =
    useState<HTMLElement | null>(null);
  const [contextTabIndex, setContextTabIndex] = useState<number | null>(null);
  const [contextOpen, setContextOpen] = useState(false);
  const [isSelectionLimitModalOpen, setIsSelectionLimitModalOpen] = useState(false);
  const [isMasterPoolApprovalModal, setMasterPoolApprovalModal] = useState(false);
  const [isMasterPoolFinalizeModal, setMasterPoolFinalizeModal] = useState(false);
  const [isMasterPoolResetWorkflowModal, setMasterPoolResetWorkflowModal] = useState(false);
  const [isMasterPoolReverseFinalizationModal, setIsMasterPoolReverseFinalizationModal] =
    useState(false);
  const [isMasterPoolReverseWithDrawModal, setIsMasterPoolReverseWithDrawModal] = useState(false);
  const [isMasterPoolOfferDateModal, setIsMasterPoolOfferDateModal] = useState(false);
  const [isReverseApproval, setIsReverseApproval] = useState(false);
  const [isMasterPoolApprovalDateModal, setIsMasterPoolApprovalDateModal] = useState(false);
  const [isMasterPoolNotification, setMasterPoolNotification] = useState(false);
  const [showToast, setShowToast] = useState(false);
  const [isOfferDate, setIsOfferDate] = useState(false);
  const [anchorEl, setAnchorEl] = useState(null);
  const [closePageModal, setClosePageModal] = useState(false);
  const [remarks, setWithdrawlOption] = useState<Remark | null>(null);
  const [isWithdrawDropDownError, setIsWithDrawError] = useState(false);
  const [isOtherOption, setOtherOption] = useState(false);
  const [isWorkflowActions, setIsWorkflowActions] = useState(false);
  const [withDrawErrorMessege, setWithDrawErrorMessege] = useState<string>(t('Character-Limit'));
  const [isWithDrawLargeFieldError, setIsWithDrawLargeFieldError] = useState<boolean>(false);
  const [toastNotificationConfig, setToastNotificationConfig] = useState<ToastNotificationConfig>({
    header: '',
    subHeader: String(' ')
  });
  const [notificationBannerConfig, setNotificationBannerConfig] = useState<ToastNotificationConfig>(
    { header: '', subHeader: '' }
  );
  const [isForceStatusTriggered, setIsForceStatusTriggered] = useState<boolean>(false);
  const [offerDateConfig, setOfferDateConfig] = useState<OfferDateConfig>({});
  const [queryParams, setQueryParams] = useState<{ [key: string]: string }>({});
  const [isMasterPoolCitadalDateModal, setIsMasterPoolCitadalDateModal] = useState(false);
  const openFinalizeModal = () => setMasterPoolFinalizeModal(true);

  const showWorkflowInProgressToast = () => {
    setToastNotificationConfig({
      header: t('Master-Pool-Details-Workflow-Inprogress-Notification-Header'),
      subHeader: t('Master-Pool-Details-Workflow-Inprogress-Notification-Body')
    });
    setShowToast(true);
  };

  const handleWorkflowConflictError = (error: unknown, fallback: () => void) => {
    if (isWorkflowConflictError(error)) {
      showWorkflowInProgressToast();
      return;
    }

    fallback();
  };

  useEffect(() => {
    if (!isMasterPoolNotification && isForceStatusTriggered) {
      setIsForceStatusTriggered(false);
    }
  }, [isMasterPoolNotification, isForceStatusTriggered]);

  const toggleMasterPoolNumberRemarksDrawer = (newOpen: boolean) => {
    setOpenMasterPoolNumberRemarksFlyout(newOpen);
    showToast && newOpen && setShowToast(false);
    isMasterPoolNotification && newOpen && setMasterPoolNotification(false);
  };

  const toggleReRunPricingScenarioModal = (newOpen: boolean) => {
    setOpenReRunPricingScenarioModal(newOpen);
    showToast && newOpen && setShowToast(false);
    isMasterPoolNotification && newOpen && setMasterPoolNotification(false);
  };

  const handleCloseMenu = () => {
    setAnchorEl(null);
  };
  const workFlowActionsClose = () => {
    setContextAnchorWorkFlowActions(null);
    setIsWorkflowActions(false);
  };
  const openReverseWithdrawModal = () => {
    setIsMasterPoolReverseWithDrawModal(true);
  };

  const handleResetWorkflowState = async () => {
    if (!isMasterPoolResetWorkflowModal) {
      setMasterPoolResetWorkflowModal(true);
      workFlowActionsClose();
      handleCloseMenu();
      return;
    }

    closeMasterPoolResetWorkflowModal();
    try {
      const response = await resetWorkflowState({
        cmhcAccountNumber: masterPoolCmhcAccountNumber
      });

      if (response) {
        setToastNotificationConfig({
          header: t('Reset-Workflow-State-In-Progress'),
          subHeader: t('Master-Pool-Details-Approval-Notification-Body')
        });
        setShowToast(true);
        return;
      }

      setNotificationBannerConfig({
        header: t('Reset-Workflow-State-Failed'),
        subHeader: t('Master-Pool-Details-Notification-Error-Body')
      });
      setMasterPoolNotification(true);
    } catch (error) {
      setNotificationBannerConfig({
        header: t('Reset-Workflow-State-Failed'),
        subHeader: t('Master-Pool-Details-Notification-Error-Body')
      });
      setMasterPoolNotification(true);
    }
  };

  // Context Menu
  const closeTabStyles =
    contextTabIndex === 0
      ? ({
          color: '#999',
          pointerformatAmountWithDollarEvents: 'none',
          cursor: 'not-allowed'
        } as React.CSSProperties)
      : undefined;

  const closeTabFn = () => {
    if (contextTabIndex !== null) handleCloseTab(contextTabIndex);
    setContextOpen(false);
  };

  const showModal = () => {
    setOtherOption(false);
    setIsWithDrawError(false);
    setClosePageModal(true);
  };

  const openWithdrawalDateModal = () => {
    setWithdrawlOption(null);
    showModal();
  };
  const openReverseFinalizationModal = () => {
    setIsMasterPoolReverseFinalizationModal(true);
  };

  const closeOtherTabsFn = () => {
    if (contextTabIndex !== null) {
      if (contextTabIndex === 0) {
        setPmRequestOverviewTabList([]);
        setActiveTab(0);
      } else {
        const tabListIndex = contextTabIndex - 1;
        setPmRequestOverviewTabList((prevTabs) => {
          const updatedTabs = [prevTabs[tabListIndex]];
          setActiveTab(1);
          return updatedTabs;
        });
      }
      setContextOpen(false);
    }
  };

  const closeAllTabsFn = () => {
    setPmRequestOverviewTabList([]);
    setActiveTab(0);
    setContextOpen(false);
  };

  const contextOptions = useRightClickContextMenu({
    closeTabFn,
    closeTabStyles,
    closeOtherTabsFn,
    closeAllTabsFn
  });

  const handleContextMenu = (event: React.MouseEvent, tabIndex: number) => {
    event.preventDefault();
    setContextAnchorEl(event.currentTarget as HTMLElement);
    setContextTabIndex(tabIndex);
    setContextOpen(true);
  };

  const openWorkFlows = (event: React.MouseEvent) => {
    event.preventDefault();
    setContextAnchorWorkFlowActions(event.currentTarget as HTMLElement);
    setIsWorkflowActions(true);
  };

  // Tabs & View Handlers
  const handleTabChange = useCallback((newValue: number) => {
    setActiveTab(newValue);
  }, []);

  const handleCloseTab = (index: number) => {
    if (index === 0) return;
    let newActiveTabIndex = activeTab;
    if (newActiveTabIndex === index) newActiveTabIndex = 0;
    else if (newActiveTabIndex > index) newActiveTabIndex--;

    setPmRequestOverviewTabList((prevTabs) => prevTabs.filter((_, i) => i + 1 !== index));
    setActiveTab(newActiveTabIndex);
    setContextOpen(false);
  };

  const handleViewLoanDetailsActionRowClick = (newTab: TabFields) => {
    const tabIndex = pmRequestOverviewTabList.findIndex(
      (tab) => tab.label.value === newTab.label.value
    );
    if (tabIndex !== -1) {
      setActiveTab(tabIndex + 1);
      return;
    }
    const updatedTabs = [...pmRequestOverviewTabList, newTab];
    if (updatedTabs.length > 10) {
      setIsSelectionLimitModalOpen(true);
      return;
    }
    setPmRequestOverviewTabList(updatedTabs);
    setActiveTab(updatedTabs.length);
  };

  const handleMutipleLoanDetailsActionClick = (newTabs: TabFields[]) => {
    const tabs = [...pmRequestOverviewTabList];
    newTabs.forEach((newTab) => {
      if (!tabs.some((tab) => tab.label.value === newTab.label.value)) {
        tabs.push(newTab);
      }
    });
    if (tabs.length > 10) {
      setIsSelectionLimitModalOpen(true);
      return;
    }
    const tabIndex = tabs.findIndex((tab) => tab.label.value === newTabs[0].label.value);
    setActiveTab(tabIndex !== -1 ? tabIndex + 1 : tabs.length);
    setPmRequestOverviewTabList(tabs);
  };

  const handleWithDrawlErrorMessages = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const newText = e.target.value;
    setWithdrawlOption((prev) => ({
      ...prev,
      remarkText: newText
    }));

    if (!newText) {
      setWithDrawErrorMessege(t('Large-Form-Error'));
      setIsWithDrawLargeFieldError(true);
    } else if (newText.length > WithdrawalMaxValue) {
      setWithDrawErrorMessege(t('Character-Limit'));
      setIsWithDrawLargeFieldError(true);
    } else {
      setWithDrawErrorMessege('');
      setIsWithDrawLargeFieldError(false);
    }
  };

  const defaultTab: TabFields = {
    label: { value: t('Loans-Inventory') },
    content: (
      <HbtMasterPoolLoansInventoryTable
        masterPoolAccountNumber={masterPoolCmhcAccountNumber}
        handleViewLoanDetailsActionRowClick={handleViewLoanDetailsActionRowClick}
        handleMutipleLoanDetailsActionClick={handleMutipleLoanDetailsActionClick}
        setMasterPoolNotification={setMasterPoolNotification}
        setNotificationBannerConfig={setNotificationBannerConfig}
        setIsForceStatusTriggered={setIsForceStatusTriggered}
      />
    )
  };

  // Date Change
  const handleDateChange = (date: string | null) => {
    setOfferDateConfig((prev: OfferDateConfig) => ({
      ...prev,
      newDate: date ?? undefined
    }));
  };

  const hasReloadedRef = useRef<boolean>(false);

  const triggerOfferDateReload = () => {
    if (hasReloadedRef.current) return;
    hasReloadedRef.current = true;
    setShowToast(false);
  };

  useEffect(() => {
    if (showToast) {
      hasReloadedRef.current = false;
    }
  }, [showToast]);

  const approveCitadalDateFlow = async () => {
    try {
      if (!offerDateConfig.newDate) {
        return;
      }
      const repsonse = await citadalDate({
        cmhcAccountNumber: masterPoolCmhcAccountNumber,
        date: offerDateConfig.newDate?.toString() || ''
      });
      if (repsonse) {
        closeDates();
        setToastNotificationConfig({
          header: offerDateConfig.date
            ? t('Fraud-Detection-Date-Override-Progress')
            : t('Fraud-Detection-Date-Entry-Progress'),
          subHeader: t('Master-Pool-Details-Approval-Notification-Body')
        });
        setShowToast(true);
      }
    } catch (error) {
      handleWorkflowConflictError(error, () => {
        setNotificationBannerConfig({
          header: t('Master-Pool-Details-Notification-Error-Header'),
          subHeader: t('Master-Pool-Details-Notification-Error-Body')
        });
        setMasterPoolNotification(true);
      });
    }
    setIsMasterPoolCitadalDateModal(false);
  };

  const approveOfferDateFlow = async () => {
    try {
      const repsonse = await offerDate({
        cmhcAccountNumber: masterPoolCmhcAccountNumber,
        date: offerDateConfig.newDate?.toString() || ''
      });
      if (repsonse) {
        closeDates();
        setToastNotificationConfig({
          header: t('Offer-Date-Overridden'),
          subHeader: t('Master-Pool-Details-Approval-Notification-Body')
        });
        setShowToast(true);
      }
    } catch (error) {
      handleWorkflowConflictError(error, () => {
        setNotificationBannerConfig({
          header: t('Master-Pool-Details-Notification-Error-Header'),
          subHeader: t('Master-Pool-Details-Notification-Error-Body')
        });
        setMasterPoolNotification(true);
      });
    }
    setIsMasterPoolOfferDateModal(false);
  };

  const approveApprovalDateFlow = async () => {
    try {
      const response = await approvalDate({
        cmhcAccountNumber: masterPoolCmhcAccountNumber,
        date: offerDateConfig.newDate?.toString() || ''
      });
      if (response) {
        closeDates();
        setToastNotificationConfig({
          header: t('Confirm-Approval-Date-Override-Approve-Toast-Header'),
          subHeader: t('Master-Pool-Details-Approval-Notification-Body')
        });
        setShowToast(true);
      }
    } catch (error) {
      handleWorkflowConflictError(error, () => {
        setNotificationBannerConfig({
          header: t('Master-Pool-Details-Notification-Error-Header'),
          subHeader: t('Master-Pool-Details-Notification-Error-Body')
        });
        setMasterPoolNotification(true);
      });
    }
    setIsMasterPoolApprovalDateModal(false);
  };

  const approveMasterPool = async () => {
    closeMasterPoolApprovalModal();
    try {
      const result = await approve({
        cmhcAccountNumber: masterPoolCmhcAccountNumber
      });
      if (result) {
        setToastNotificationConfig({
          header: t('Master-Pool-Details-Approval-Notification-Header'),
          subHeader: t('Master-Pool-Details-Approval-Notification-Body')
        });
        setQueryParams((prev) => ({
          ...prev,
          masterPoolAccountStatusCode: PortfolioLoanStatusCode.Approved
        }));
        setShowToast(true);
      }
    } catch (error) {
      handleWorkflowConflictError(error, () => {
        setNotificationBannerConfig({
          header: t('Master-Pool-Details-Notification-Error-Header'),
          subHeader: t('Master-Pool-Details-Notification-Error-Body')
        });
        setMasterPoolNotification(true);
      });
    }
    setIsMasterPoolOfferDateModal(false);
  };

  const finalizeMasterPool = async () => {
    closeFinalizeModal();
    try {
      const result = await finalize({
        cmhcAccountNumber: masterPoolCmhcAccountNumber
      });
      if (result) {
        closeDates();
        setToastNotificationConfig({
          header: t('Master-Pool-Details-Finalize-Toast-Header'),
          subHeader: t('Master-Pool-Details-Approval-Notification-Body')
        });
        setQueryParams((prev) => ({
          ...prev,
          masterPoolAccountStatusCode: PortfolioLoanStatusCode.Finalized
        }));

        setShowToast(true);
      }
    } catch (error) {
      handleWorkflowConflictError(error, () => {
        setNotificationBannerConfig({
          header: t('Master-Pool-Details-Finalize-Notification-Header'),
          subHeader: t('Master-Pool-Details-Notification-Error-Body')
        });
        setMasterPoolNotification(true);
      });
    }
    setIsMasterPoolOfferDateModal(false);
  };

  const finalizeReRunPricingScenario = async () => {
    closeReRunPricingScenarioModal();
    try {
      const result = await reRunPricingScenario({
        cmhcAccountNumber: masterPoolCmhcAccountNumber
      });
      if (result) {
        setToastNotificationConfig({
          header: t('Master-Pool-Details-ReRun-Toast-Header'),
          subHeader: t('Master-Pool-Details-ReRun-Toast-Body')
        });
        setShowToast(true);
      }
    } catch (error) {
      setNotificationBannerConfig({
        header: t('Master-Pool-Details-ReRun-Notification-Header'),
        subHeader: t('Master-Pool-Details-Notification-Error-Body')
      });
      setMasterPoolNotification(true);
    }
  };

  const reverseApprovalMasterPool = async (event: React.MouseEvent) => {
    event.preventDefault();
    closeReverseApproval();
    try {
      const result = await reverse({
        cmhcAccountNumber: masterPoolCmhcAccountNumber
      });
      if (result) {
        setToastNotificationConfig({
          header: t('Reverse-Approval-in-Progress'),
          subHeader: t('Master-Pool-Details-Approval-Notification-Body')
        });
        setQueryParams((prev) => ({
          ...prev,
          masterPoolAccountStatusCode: PortfolioLoanStatusCode.Risked
        }));

        setShowToast(true);
      }
    } catch (error) {
      handleWorkflowConflictError(error, () => {
        setNotificationBannerConfig({
          header: t('Reverse-Approval-Failed'),
          subHeader: t('Master-Pool-Details-Notification-Error-Body')
        });
        setMasterPoolNotification(true);
      });
    }
    setIsMasterPoolOfferDateModal(false);
  };

  const convertWithdrawIntoArray = (): WithdrawalOption[] => {
    const withdrawalOptions = withdrawalValues.map((value) => ({
      value,
      label: t(value),
      isGroup: false,
      groupName: ''
    }));

    return withdrawalOptions;
  };

  useEffect(() => {
    if (!queryParams.masterPoolAccountStatusCode) return;

    const params = new URLSearchParams(location.search);
    params.set('masterPoolAccountStatusCode', queryParams.masterPoolAccountStatusCode);

    const newUrl = `${window.location.pathname}?${params.toString()}`;
    history.push(newUrl);
  }, [queryParams.masterPoolAccountStatusCode]);

  const [openDateModalIntent, setOpenDateModalIntent] = useState({
    value: false,
    modalType: ''
  });

  //  Modal Closers
  const closeDates = () => setIsOfferDate(false);
  const closeFinalizeModal = () => setMasterPoolFinalizeModal(false);
  const closeMasterPoolResetWorkflowModal = () => setMasterPoolResetWorkflowModal(false);
  const closeMasterPoolApprovalModal = () => setMasterPoolApprovalModal(false);
  const closeMasterPoolOfferDateModal = () => setIsMasterPoolOfferDateModal(false);
  const closeApprovalDateModal = () => setIsMasterPoolApprovalDateModal(false);
  const showApprovalOfferDateModal = () => {
    setOpenDateModalIntent({ value: true, modalType: 'offerApprovalDate' });
  };

  const showApprovalDateModal = () => {
    setOpenDateModalIntent({ value: true, modalType: 'approvalDate' });
  };

  const openMasterPoolModal = () => setMasterPoolApprovalModal(true);
  const reverseApproval = () => setIsReverseApproval(true);
  const closeReverseApproval = () => setIsReverseApproval(false);
  const closeReRunPricingScenarioModal = () => toggleReRunPricingScenarioModal(false);

  const closeMasterPoolCitadalDateModal = () => setIsMasterPoolCitadalDateModal(false);

  const showCitadalDateModal = () => {
    setOpenDateModalIntent({ value: true, modalType: 'citadelDate' });
  };

  useEffect(() => {
    if (!openDateModalIntent.value) return;
    const dateError = !offerDateConfig.newDate;
    if (dateError) {
      setToastNotificationConfig({
        header: t('Master-Pool-Details-Date-Error-Header'),
        subHeader: t('Master-Pool-Details-Date-Error-SubHeader')
      });
      setShowToast(true);
      setOpenDateModalIntent({ value: false, modalType: '' });
      return;
    }
    openDateModalIntent.modalType === 'citadelDate' && setIsMasterPoolCitadalDateModal(true);
    openDateModalIntent.modalType === 'offerApprovalDate' && setIsMasterPoolOfferDateModal(true);
    openDateModalIntent.modalType === 'approvalDate' && setIsMasterPoolApprovalDateModal(true);
    setOpenDateModalIntent({ value: false, modalType: '' });
  }, [openDateModalIntent, offerDateConfig.date, offerDateConfig.newDate]);

  const popperModifiers = [
    {
      name: 'offset',
      options: {
        offset: [-182, 0]
      }
    },
    {
      name: 'flip',
      options: {
        fallbackPlacements: ['top-start', 'bottom-start']
      }
    },
    {
      name: 'flip',
      options: {
        fallbackPlacements: ['top-start', 'bottom-start']
      }
    }
  ];

  const options = validateWorkFlowOptions({
    t,
    masterPoolAccountStatusCode,
    checkForApprovalDateRules,
    checkForWithdrawRules,
    reverseApproval,
    checkForReverseFinalizationRules,
    checkForReverseWithdrawRules,
    openWithdrawalDateModal,
    openReverseFinalizationModal,
    openReverseWithdrawModal,
    handleCloseMenu,
    styles
  });

  const handleViewHistoricalRemarks = () => {
    toggleHistoricalRemarksDrawer(true);
  };
  const handleAddModifyRemarks = () => {
    toggleMasterPoolNumberRemarksDrawer(true);
  };

  if (remarksResponse && remarksResponse?.length > 0) {
    options.unshift(
      createOption(
        t('Masterpool-Flyout-View-Historical-Remark'),
        t('Masterpool-Flyout-View-Historical-Remark'),
        'icon_comment',
        handleViewHistoricalRemarks,
        styles,
        '',
        true
      )
    );
  }

  options.unshift(
    createOption(
      t('Masterpool-Flyout-Add-Remark'),
      t('Masterpool-Flyout-Add-Remark'),
      'icon_comment_add',
      handleAddModifyRemarks,
      styles,
      '',
      true
    )
  );

  if (masterPoolAccountStatusCode === MasterPoolStatusEnum.Risked) {
    options.push(
      createOption(
        t('Masterpool-Modal-Re-Run-Pricing-Scenarios'),
        t('Masterpool-Modal-Re-Run-Pricing-Scenarios'),
        'icon_calculator',
        () => toggleReRunPricingScenarioModal(true),
        styles,
        '',
        true
      )
    );
  }

  options.push(
    createOption(
      t('Reset-Workflow-State'),
      t('Reset-Workflow-State'),
      <span className={styles.iconResetSpacing}>
        <ResetWorkflowStateIcon />
      </span>,
      handleResetWorkflowState,
      styles,
      '',
      true
    )
  );

  const handleAddModifyRemarksToastNotification = (
    error: boolean,
    message: { header: string; subHeader: string }
  ) => {
    if (error) {
      setMasterPoolNotification(true);
      setNotificationBannerConfig({
        header: message.header,
        subHeader: message.subHeader
      });
    } else {
      setShowToast(true);
      refetch();
      setToastNotificationConfig({
        header: message.header,
        subHeader: message.subHeader
      });
    }
  };

  return (
    <>
      <Helmet>
        <title>{t('title')}</title>
      </Helmet>

      <HbtBreadcrumbComponent
        {...t('breadcrumb', { returnObjects: true, searchParams })}
        ids={{
          accountNum: masterPoolCmhcAccountNumber,
          masterPoolAccountStatus: masterPoolAccountStatusCode
        }}
        skippedHrefs={skippedHrefs}
      />

      <Box className={styles.masterPoolDetailsWrapper}>
        <div className={styles.notificationBanner}>
          <NotificationBanner
            key={`notification-banner-${isMasterPoolNotification}`}
            showNotificationBanner={isMasterPoolNotification}
            onCloseNotificationBanner={() => setMasterPoolNotification(!isMasterPoolNotification)}
            headerText={{ value: notificationBannerConfig.header }}
            message={{ value: notificationBannerConfig.subHeader }}
            variant={
              isForceStatusTriggered ? NotificationBannerType.INFO : NotificationBannerType.ERROR
            }
          />
        </div>
        <Box className={styles.applicationOverviewContainer}>
          <Box className={styles.masterPoolDetailsHeader}>
            <Box>
              <Box className={styles.titleContainer}>
                {/* Title + Buttons Row */}
                <Box className={styles.titleButtons}>
                  {/* Title + Status Pill */}
                  <Box className={styles.titleStatusPill}>
                    <Typography className={styles.pageHeaderText} variant="h1">
                      {t('PI-Title')} {getMasterPoolCmhcAccountNumber()}
                    </Typography>
                    {masterPoolAccountStatusCode.length > 0 && (
                      <Box className={styles.statusPill}>
                        <LoanStatusPill
                          status={masterPoolAccountStatusCode}
                          label={getLoanStatusLabel(
                            masterPoolAccountStatusCode as keyof typeof loanStatusMapping,
                            t
                          )}
                        />
                      </Box>
                    )}
                  </Box>

                  {/* Right-side buttons */}
                  <Box className={styles.rightSideButtons}>
                    <Button
                      type="button"
                      onClick={openWorkFlows}
                      name="actions"
                      size={ButtonSize.LARGE}
                      variant={ButtonVariant.TERTIARY}
                      leadingIcon={() => (
                        <CmhcIcon size={CmhcIconSize.SMALL} type="icon_more_vertical" />
                      )}
                      text={{ value: t('Btn-More') }}
                      ariaText={{ value: t('Btn-More') }}
                      className={styles.button}
                    />
                    <Box>
                      <Button
                        name="pricing-scenarios"
                        type="button"
                        onClick={() => navigateToScenarioPricing(data, i18n.language, history)}
                        size={ButtonSize.LARGE}
                        variant={ButtonVariant.SECONDARY}
                        leadingIcon={() => (
                          <CmhcIcon size={CmhcIconSize.SMALL} type="icon_calculator" />
                        )}
                        text={{ value: t('Master-Pool-Pricing-Scenario') }}
                        ariaText={{
                          value: t('Master-Pool-Pricing-Scenario')
                        }}
                      />
                    </Box>
                    {checkForApprovalRules(masterPoolAccountStatusCode) && (
                      <Button
                        type="button"
                        name="btn-approve"
                        size={ButtonSize.LARGE}
                        onClick={openMasterPoolModal}
                        variant={ButtonVariant.PRIMARY}
                        text={{ value: `${t('Btn-Approve')}` }}
                        ariaText={{ value: t('Btn-Approve') }}
                        className={styles.viewLoanDetailsButton}
                      />
                    )}
                    {checkForFinalizeRules(masterPoolAccountStatusCode) && (
                      <Button
                        type="button"
                        name="btn-finalize"
                        size={ButtonSize.LARGE}
                        onClick={openFinalizeModal}
                        variant={ButtonVariant.PRIMARY}
                        text={{ value: `${t('Btn-Finalize')}` }}
                        ariaText={{ value: t('Btn-Approve') }}
                        className={styles.viewLoanDetailsButton}
                      />
                    )}

                    {contextAnchorWorkFlowActions && (
                      <ClickAwayListener onClickAway={workFlowActionsClose}>
                        <Box className={styles.dropdownAlignment}>
                          <ActionDropdown
                            className={styles.dropDownZIndex}
                            popperModifiers={popperModifiers}
                            anchorEl={contextAnchorWorkFlowActions || undefined}
                            open={isWorkflowActions}
                            onToggle={(open) => setIsWorkflowActions(open)}
                            options={options}
                            trigger={<></>}
                          />
                        </Box>
                      </ClickAwayListener>
                    )}
                  </Box>
                </Box>

                <Box>
                  <DocumentAndResourcesFlyout
                    statusCode={masterPoolAccountStatusCode}
                    insuredLenderCode={masterPoolLenderCode}
                    masterPoolCmhcAccountNumber={masterPoolCmhcAccountNumber}
                    requestType={requestType}
                  />
                </Box>
              </Box>
            </Box>
            <MasterPoolDataRows
              data={data}
              masterPoolAccountStatusCode={masterPoolAccountStatusCode}
              setOfferDateConfig={setOfferDateConfig}
              showApprovalOfferDateModal={showApprovalOfferDateModal}
              showApprovalDateModal={showApprovalDateModal}
              showCitadalDateModal={showCitadalDateModal}
              closeDates={closeDates}
              setIsOfferDate={setIsOfferDate}
              setIsMasterPoolApprovalDateModal={setIsMasterPoolApprovalDateModal}
            />
            {isOfferDate && (
              <Box className={styles.offerDateContainerWrapper}>
                <OfferDate offerDateConfig={offerDateConfig} onDateChange={handleDateChange} />
              </Box>
            )}
          </Box>
          {masterPoolAccountStatusCode === MasterPoolStatusEnum.Risked && (
            <Modal
              id="master-pool-finalize-modal"
              isActive={openReRunPricingScenarioModal}
              title={t('Master-Pool-Details-Re-Run-Modal-Header')}
              onDismissalCallback={closeReRunPricingScenarioModal}
              bodyContent={<span>{t('Master-Pool-Details-Re-Run-Modal-Body')}</span>}
              modalButtons={{
                primaryButton: {
                  name: t('Masterpool-Modal-Re-Run-Pricing-Scenarios'),
                  text: {
                    value: t('Masterpool-Modal-Re-Run-Pricing-Scenarios')
                  } as FieldValue,
                  ariaText: {
                    value: t('Masterpool-Modal-Re-Run-Pricing-Scenarios')
                  } as FieldValue,
                  onClick: () => {
                    finalizeReRunPricingScenario();
                  }
                },
                secondaryButton: {
                  name: t('Cancel'),
                  text: { value: t('Cancel') } as FieldValue,
                  ariaText: { value: t('Cancel') } as FieldValue,
                  onClick: () => closeReRunPricingScenarioModal()
                }
              }}
              shouldCloseOnBackgroundClick={true}
            />
          )}
          <Modal
            id="master-pool-finalize-modal"
            isActive={isMasterPoolFinalizeModal}
            title={t('Master-Pool-Details-Finalize-Modal-Header')}
            onDismissalCallback={closeFinalizeModal}
            bodyContent={
              <RichTextBody translationKey={t('Master-Pool-Details-Finalize-Modal-Body')} />
            }
            modalButtons={{
              primaryButton: {
                name: t('Btn-Finalize'),
                text: { value: t('Btn-Finalize') } as FieldValue,
                ariaText: { value: t('Btn-Finalize') } as FieldValue,
                onClick: () => {
                  finalizeMasterPool();
                }
              },
              secondaryButton: {
                name: t('Cancel'),
                text: { value: t('Cancel') } as FieldValue,
                ariaText: { value: t('Cancel') } as FieldValue,
                onClick: closeFinalizeModal
              }
            }}
            shouldCloseOnBackgroundClick={true}
          />
          <Modal
            id="master-pool-reset-workflow-state-modal"
            isActive={isMasterPoolResetWorkflowModal}
            title={t('Confirm-Reset-Workflow-Modal-Title')}
            onDismissalCallback={closeMasterPoolResetWorkflowModal}
            bodyContent={
              <Box className={styles.resetWorkflowModalBody}>
                <RichTextBody translationKey={t('Confirm-Reset-Workflow-Modal-Body')} />
              </Box>
            }
            modalButtons={{
              primaryButton: {
                name: t('Reset-Workflow-State'),
                text: { value: t('Reset-Workflow-State') } as FieldValue,
                ariaText: { value: t('Reset-Workflow-State') } as FieldValue,
                onClick: () => {
                  handleResetWorkflowState();
                }
              },
              secondaryButton: {
                name: t('Cancel'),
                text: { value: t('Cancel') } as FieldValue,
                ariaText: { value: t('Cancel') } as FieldValue,
                onClick: closeMasterPoolResetWorkflowModal
              }
            }}
            shouldCloseOnBackgroundClick={true}
          />
          <Modal
            id="master-pool-reverse-approval-modal"
            isActive={isReverseApproval}
            title={t('Confirm-Reverse-Approval')}
            onDismissalCallback={closeReverseApproval}
            bodyContent={<RichTextBody translationKey={t('Confirm-Reverse-Approval-Body')} />}
            modalButtons={{
              primaryButton: {
                name: t('Reverse-Approval'),
                text: { value: t('Reverse-Approval') } as FieldValue,
                ariaText: { value: t('Reverse-Approval') } as FieldValue,
                onClick: (event: React.MouseEvent) => {
                  reverseApprovalMasterPool(event);
                }
              },
              secondaryButton: {
                name: t('Cancel'),
                text: { value: t('Cancel') } as FieldValue,
                ariaText: { value: t('Cancel') } as FieldValue,
                onClick: () => {
                  closeReverseApproval();
                }
              }
            }}
            shouldCloseOnBackgroundClick={true}
          />
          <Modal
            id="master-pool-approval-modal"
            isActive={isMasterPoolApprovalModal}
            title={t('Master-Pool-Approval-Header')}
            onDismissalCallback={closeMasterPoolApprovalModal}
            bodyContent={<RichTextBody translationKey={t('Master-Pool-Approval-Body')} />}
            modalButtons={{
              primaryButton: {
                name: t('Master-Pool-Approval-Approve-Btn'),
                text: {
                  value: t('Master-Pool-Approval-Approve-Btn')
                } as FieldValue,
                ariaText: {
                  value: t('Master-Pool-Approval-Approve-Btn')
                } as FieldValue,
                onClick: () => {
                  approveMasterPool();
                }
              },
              secondaryButton: {
                name: t('Cancel'),
                text: { value: t('Cancel') } as FieldValue,
                ariaText: { value: t('Cancel') } as FieldValue,
                onClick: closeMasterPoolApprovalModal
              }
            }}
            shouldCloseOnBackgroundClick={true}
          />
          <Modal
            id="master-pool-approval-date-modal"
            isActive={isMasterPoolApprovalDateModal}
            title={t('Confirm-Approval-Date-Override')}
            onDismissalCallback={closeApprovalDateModal}
            bodyContent={<span>{t('Confirm-Approval-Date-Override-Body')}</span>}
            modalButtons={{
              primaryButton: {
                name: t('Override-Approval-Date'),
                text: {
                  value: t('Override-Approval-Date')
                } as FieldValue,
                ariaText: {
                  value: t('Override-Approval-Date')
                } as FieldValue,
                onClick: () => approveApprovalDateFlow()
              },
              secondaryButton: {
                name: t('Cancel'),
                text: { value: t('Cancel') } as FieldValue,
                ariaText: { value: t('Cancel') } as FieldValue,
                onClick: () => closeApprovalDateModal()
              }
            }}
            shouldCloseOnBackgroundClick={true}
          />
          <Modal
            id="master-pool-offer-date-modal"
            isActive={isMasterPoolOfferDateModal}
            title={t('Confirm-Offer-Date-Override')}
            onDismissalCallback={closeMasterPoolOfferDateModal}
            bodyContent={<span>{t('Confirm-Offer-Date-Override-Body')}</span>}
            modalButtons={{
              primaryButton: {
                name: t('Overide-Offer-Date-Text'),
                text: {
                  value: t('Overide-Offer-Date-Text')
                } as FieldValue,
                ariaText: {
                  value: t('Overide-Offer-Date-Text')
                } as FieldValue,
                onClick: () => approveOfferDateFlow()
              },
              secondaryButton: {
                name: t('Cancel'),
                text: { value: t('Cancel') } as FieldValue,
                ariaText: { value: t('Cancel') } as FieldValue,
                onClick: () => closeMasterPoolOfferDateModal()
              }
            }}
            shouldCloseOnBackgroundClick={true}
          />
          <Modal
            id="master-pool-citadal-date-modal"
            isActive={isMasterPoolCitadalDateModal}
            title={t('Confirm-Fraud-Detection-Date')}
            onDismissalCallback={closeMasterPoolCitadalDateModal}
            bodyContent={<span>{t('Confirm-Fraud-Detection-Message')}</span>}
            modalButtons={{
              primaryButton: {
                name: offerDateConfig.date
                  ? t('Override-Fraud-Detection-Date')
                  : t('Enter-Fraud-Detection-Date'),
                text: {
                  value: offerDateConfig.date
                    ? t('Override-Fraud-Detection-Date')
                    : t('Enter-Fraud-Detection-Date')
                } as FieldValue,
                ariaText: {
                  value: offerDateConfig.date
                    ? t('Override-Fraud-Detection-Date')
                    : t('Enter-Fraud-Detection-Date')
                } as FieldValue,
                onClick: () => approveCitadalDateFlow()
              },
              secondaryButton: {
                name: t('Cancel'),
                text: { value: t('Cancel') } as FieldValue,
                ariaText: { value: t('Cancel') } as FieldValue,
                onClick: () => closeMasterPoolCitadalDateModal(),
                onKeyDown: () => {}
              }
            }}
            shouldCloseOnBackgroundClick={true}
          />

          {/* Accordion Section */}
          <Box className={styles.headerAccordionSectionWrapper}>
            <Accordion
              suppressBottomBorder
              accordionData={{
                heading: t('Application-Details'),
                defaultExpanded: true,
                contentSlot: (
                  <Box className={styles.requestOverviewContainer}>
                    <ApplicationOverviewDetails
                      showSpinner={isLoading && !isError}
                      showErrorMessage={!isLoading && isError}
                      className={`${isLoading || isError ? styles.requestDetailsErrorState : ''}`}
                      data={convertMasterPool}
                      pptCode={convertMasterPool?.requestType?.toString()}
                      isCancellationLoan={false}
                      title={t('PI-Portfolio-Request-Overview-Application')}
                      options={{
                        insuredApprovedLenderCode: false,
                        insuredApprovedLenderName: false
                      }}
                      termInsuranceToolTipBody={t('Application-Overview-Details-ToolTip-Body')}
                      totalLoansSubmittedToolTipBody={t('Total-Loans-Submitted-ToolTip-Body')}
                      isItMasterPool={true}
                    />

                    <ApplicationLoanCompositionDetails
                      showSpinner={isLoanLoading && !isLoanError}
                      showErrorMessage={!isLoanLoading && (showLoanCompositionError || isLoanError)}
                      className={`${
                        isLoanLoading || isLoanError ? styles.requestDetailsErrorState : ''
                      }`}
                      data={convertedLoansDetails}
                      isResultsCollapsed={false}
                      loanSummaryError={loanSummaryError ?? (isLoanError && 'api_error')}
                      isCancellationLoan={false}
                      title={t('Loan-Composition')}
                      isMasterPoolLoanComposition={true}
                    />
                  </Box>
                )
              }}
            />
          </Box>
          {(shouldShowPremiumsAndTaxes || noDataAvailable) && !shouldHidePremiumsAndTaxes && (
            <Box className={styles.masterPoolPremiums}>
              <HbtMasterPoolPremiumsAndTaxes
                shouldShowPremiumsAndTaxes={shouldShowPremiumsAndTaxes}
                noDataAvailable={noDataAvailable}
                premiumAndTaxesData={premiumAndTaxesData}
                premiumAndTaxesDataLoading={premiumAndTaxesDataLoading}
                premiumAndTaxesError={premiumAndTaxesError}
              />
            </Box>
          )}
          <Box>
            {/* Loans Inventory */}
            <Typography className={styles.header} variant="h1">
              <div className={styles.loanManagementHeading}>{t('Loan-Inventory')}</div>
            </Typography>
            <Tab
              tabs={[defaultTab, ...pmRequestOverviewTabList]}
              tabsType={TabsType.FULL_WIDTH}
              initialActiveTabIndex={activeTab}
              onTabChange={handleTabChange}
              onTabRightClick={handleContextMenu}
              showCloseButton={(_: unknown, index: number) => index}
              onTabClose={(index: number) => {
                handleCloseTab(index);
              }}
              tabWidthAuto
            />
            {contextAnchorEl && (
              <div className={styles.closeTabMenu}>
                <ActionDropdown
                  anchorEl={contextAnchorEl}
                  open={contextOpen}
                  onToggle={(open) => setContextOpen(open)}
                  options={contextOptions}
                  trigger={<></>}
                />
              </div>
            )}
          </Box>
        </Box>
        <ToastNotification
          onClose={() => {
            triggerOfferDateReload();
          }}
          icon="icon_check_circle"
          dismissMode={'actionable'}
          variant={ToastNotificationVariant.INFO}
          open={showToast}
          header={{ value: toastNotificationConfig.header }}
          subHeader={{ value: toastNotificationConfig.subHeader }}
        />
        <Modal
          id={'selction-limit-reached'}
          isActive={isSelectionLimitModalOpen}
          onDismissalCallback={() => setIsSelectionLimitModalOpen(false)}
          title={{ value: ro('Selection-Limit-Reached') }}
          bodyContent={ro('Selection-Limit-Body-Text')}
          shouldCloseOnBackgroundClick={true}
        />
        <Modal
          id="masterpool-withdrawal-modal"
          isActive={closePageModal}
          title={t('Confirm-Withdrawal')}
          onDismissalCallback={() => setClosePageModal(false)}
          shouldCloseOnBackgroundClick={true}
          bodyContent={
            <Box>
              <div>
                <RichTextBody translationKey={t('Confirm-Withdrawl-Body')} />
                <p>{t('Reason-For-Withdrawl')}</p>
                <ComboBox
                  id="withdraw-combobox"
                  ariaText={{ value: t('Add-Remarks-Required-Error-Msg') }}
                  className={styles.withDrawCombobox}
                  placeholderText={t('Select')}
                  errorMessage={t('Required-WithDraw-DropDown')}
                  options={convertWithdrawIntoArray()}
                  value={remarks?.remarkId?.toString()}
                  error={isWithdrawDropDownError}
                  onChange={(_event, option: ComboboxOption | null) => {
                    setIsWithDrawError(false);
                    setIsWithDrawLargeFieldError(false);
                    setWithDrawErrorMessege('');

                    if (option) {
                      const isOther = option.value === WithdrawalRemarks.OTHER;
                      setOtherOption(isOther);
                      setWithdrawlOption({
                        remarkId: Number(option.value),
                        remarkText: !isOther ? option.label : ''
                      });
                    } else {
                      setWithdrawlOption(null);
                    }
                  }}
                />

                {isOtherOption && (
                  <>
                    <div className={styles.paddingTop}>
                      <LargeFormField
                        errorMessage={withDrawErrorMessege}
                        maxLength={WithdrawalMaxValue}
                        minLength={0}
                        status={isWithDrawLargeFieldError ? 'error' : 'success'}
                        id="withdraw"
                        value={remarks?.remarkText || ''}
                        onChange={(e) => handleWithDrawlErrorMessages(e)}
                      />
                    </div>
                  </>
                )}
              </div>
            </Box>
          }
          modalButtons={{
            primaryButton: {
              name: 'primary',
              text: { value: t('Withdraw') },
              ariaText: { value: t('Withdraw') },
              onClick: async (event: React.MouseEvent) => {
                event.stopPropagation();
                try {
                  if (!remarks) {
                    setIsWithDrawError(true);
                    return;
                  }
                  if (remarks.remarkText && remarks?.remarkText?.length > WithdrawalMaxValue) {
                    setWithDrawErrorMessege(t('Character-Limit'));
                    return;
                  }
                  if (isOtherOption && remarks?.remarkText?.length === 0) {
                    setWithDrawErrorMessege(t('Large-Form-Error'));
                    setIsWithDrawLargeFieldError(true);
                    return;
                  }
                  setClosePageModal(false);
                  const response = await withdrawMasterPool({
                    cmhcAccountNumber: masterPoolCmhcAccountNumber,
                    remarks
                  });

                  if (response) {
                    setToastNotificationConfig({
                      header: t('Withdrawl-In-Progress'),
                      subHeader: t('Master-Pool-Details-Approval-Notification-Body')
                    });
                    setShowToast(true);
                    setQueryParams((prev) => ({
                      ...prev,
                      masterPoolAccountStatusCode: PortfolioLoanStatusCode.Withdrawn
                    }));
                  }
                } catch (error) {
                  handleWorkflowConflictError(error, () => {
                    setNotificationBannerConfig({
                      header: t('Withdraw-failed'),
                      subHeader: t('Master-Pool-Details-Notification-Error-Body')
                    });
                    setMasterPoolNotification(true);
                  });
                }
                setClosePageModal(false);
              }
            },
            secondaryButton: {
              size: ButtonSize.SMALL,
              name: 'cancel-btn',
              text: { value: t('Cancel') },
              ariaText: { value: t('Cancel') },
              onClick: () => {
                setClosePageModal(false);
                setIsWithDrawLargeFieldError(false);
              }
            }
          }}
        />
        <Modal
          id="masterpool-reverse-finalization-modal"
          isActive={isMasterPoolReverseFinalizationModal}
          title={t('Confirm-Reverse-Finalization')}
          onDismissalCallback={() => setIsMasterPoolReverseFinalizationModal(false)}
          shouldCloseOnBackgroundClick={true}
          bodyContent={<RichTextBody translationKey={t('Reverse-Finalization-Modal-Body')} />}
          modalButtons={{
            primaryButton: {
              name: 'primary',
              text: { value: t('Reverse-Reverse-Finalization') },
              ariaText: { value: t('Reverse-Reverse-Finalization') },
              onClick: async (event: React.MouseEvent) => {
                event.preventDefault();
                setIsMasterPoolReverseFinalizationModal(false);
                try {
                  const response = await reverseFinalization({
                    cmhcAccountNumber: masterPoolCmhcAccountNumber
                  });

                  if (response) {
                    setToastNotificationConfig({
                      header: t('Reverse-Finalization-in-Progress'),
                      subHeader: t('Master-Pool-Details-Approval-Notification-Body')
                    });
                    setShowToast(true);
                    setQueryParams((prev) => ({
                      ...prev,
                      masterPoolAccountStatusCode: PortfolioLoanStatusCode.Approved
                    }));
                  }
                } catch (error) {
                  handleWorkflowConflictError(error, () => {
                    setNotificationBannerConfig({
                      header: t('Reverse-Finalization-Failed'),
                      subHeader: t('Master-Pool-Details-Notification-Error-Body')
                    });
                    setMasterPoolNotification(true);
                  });
                }
              }
            },
            secondaryButton: {
              size: ButtonSize.SMALL,
              name: 'cancel-btn',
              text: { value: t('Cancel') },
              ariaText: { value: t('Cancel') },
              onClick: () => {
                setIsMasterPoolReverseFinalizationModal(false);
              }
            }
          }}
        />

        <Modal
          id="masterpool-reverse-withdraw-modal"
          isActive={isMasterPoolReverseWithDrawModal}
          title={t('Confirm-Reverse-Withdrawl')}
          onDismissalCallback={() => setIsMasterPoolReverseWithDrawModal(false)}
          shouldCloseOnBackgroundClick={true}
          bodyContent={<RichTextBody translationKey={t('Reverse-Withdraw-body')} />}
          modalButtons={{
            primaryButton: {
              name: 'primary',
              text: { value: t('Reverse-Withdrawl') },
              ariaText: { value: t('Reverse-Withdrawl') },
              onClick: async (event: React.MouseEvent) => {
                event.preventDefault();
                setIsMasterPoolReverseWithDrawModal(false);
                try {
                  const response = await reverseWithdraw({
                    cmhcAccountNumber: masterPoolCmhcAccountNumber
                  });

                  if (response) {
                    setToastNotificationConfig({
                      header: t('Reverse-Withdrawal-in-Progress'),
                      subHeader: t('Master-Pool-Details-Approval-Notification-Body')
                    });
                    setShowToast(true);
                    setQueryParams((prev) => ({
                      ...prev,
                      masterPoolAccountStatusCode: PortfolioLoanStatusCode.Risked
                    }));
                  }
                } catch (error) {
                  handleWorkflowConflictError(error, () => {
                    setNotificationBannerConfig({
                      header: t('Reverse-Withdrawl-Failed'),
                      subHeader: t('Master-Pool-Details-Notification-Error-Body')
                    });
                    setMasterPoolNotification(true);
                  });
                }
              }
            },
            secondaryButton: {
              size: ButtonSize.SMALL,
              name: 'cancel-btn',
              text: { value: t('Cancel') },
              ariaText: { value: t('Cancel') },
              onClick: () => {
                setIsMasterPoolReverseWithDrawModal(false);
              }
            }
          }}
        />
        {remarksResponse && remarksResponse?.length > 0 && (
          <ViewHistoricalRemarksFlyout
            openFlyout={openHistoricalRemarksFlyout}
            toggleFlyout={() => {
              toggleHistoricalRemarksDrawer(false);
            }}
            selectedRowsData={[+masterPoolCmhcAccountNumber]}
            remarksResponse={remarksResponse}
            isRemarksLoading={isRemarksLoading}
            isRemarksError={isRemarksError}
          />
        )}
        <HbtMasterPoolNumberAddRemarksFlyout
          masterPoolNumber={+masterPoolCmhcAccountNumber}
          latestRemark={data?.[0]?.latestRemark ?? ({} as LatestRemark)}
          toggleToaster={handleAddModifyRemarksToastNotification}
          openFlyout={openMasterPoolNumberRemarksFlyout}
          toggleDrawer={toggleMasterPoolNumberRemarksDrawer}
        />
      </Box>
    </>
  );
};

export default memo(HbtMasterPoolDetails);
